import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class DeviceOrientationService {
    private heading$ = new BehaviorSubject<number | null>(null);
    private isListening = false;
    private orientationHandler: ((event: DeviceOrientationEvent) => void) | null = null;

    constructor(private ngZone: NgZone) {}

    /**
     * Get the current device heading as an Observable
     * Returns null if the device doesn't support orientation or permission is denied
     */
    getHeading(): Observable<number | null> {
        return this.heading$.asObservable();
    }

    /**
     * Get the current heading value synchronously
     */
    getCurrentHeading(): number | null {
        return this.heading$.value;
    }

    /**
     * Start listening to device orientation events
     * Returns a promise that resolves when permission is granted (on iOS 13+)
     */
    async startListening(): Promise<boolean> {
        if (this.isListening) {
            return true;
        }

        // Check if DeviceOrientationEvent is available
        if (typeof DeviceOrientationEvent === 'undefined') {
            console.warn('DeviceOrientationEvent is not supported on this device');
            return false;
        }

        // Request permission on iOS 13+
        if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
            try {
                const permission = await (DeviceOrientationEvent as any).requestPermission();
                if (permission !== 'granted') {
                    console.warn('Device orientation permission denied');
                    return false;
                }
            } catch (error) {
                console.error('Error requesting device orientation permission:', error);
                return false;
            }
        }

        // Start listening to device orientation
        this.orientationHandler = (event: DeviceOrientationEvent) => {
            this.ngZone.run(() => {
                const heading = this.getCompassHeading(event);
                if (heading !== null) {
                    this.heading$.next(heading);
                }
            });
        };

        window.addEventListener('deviceorientationabsolute', this.orientationHandler as any, true);
        window.addEventListener('deviceorientation', this.orientationHandler, true);

        this.isListening = true;
        return true;
    }

    /**
     * Stop listening to device orientation events
     */
    stopListening(): void {
        if (this.orientationHandler) {
            window.removeEventListener('deviceorientationabsolute', this.orientationHandler as any, true);
            window.removeEventListener('deviceorientation', this.orientationHandler, true);
            this.orientationHandler = null;
        }
        this.isListening = false;
        this.heading$.next(null);
    }

    /**
     * Extract compass heading from DeviceOrientationEvent
     * Returns heading in degrees (0-360) where 0 is North
     */
    private getCompassHeading(event: DeviceOrientationEvent): number | null {
        // For devices with absolute orientation (compass)
        if (event.absolute && event.alpha !== null) {
            // alpha is the compass heading (0 = North, 90 = East, 180 = South, 270 = West)
            // Some devices use webkitCompassHeading instead
            const webkitHeading = (event as any).webkitCompassHeading;

            if (webkitHeading !== undefined && webkitHeading !== null) {
                // webkitCompassHeading is already 0-360 with 0 = North
                return webkitHeading;
            } else if (event.alpha !== null) {
                // For standard deviceorientationabsolute, alpha is the compass heading
                // Normalize to 0-360 range
                return (360 - event.alpha) % 360;
            }
        }

        // Fallback for devices without absolute orientation
        if (event.alpha !== null && !event.absolute) {
            // This is relative orientation, not true compass heading
            // Still return it as it might be useful
            return event.alpha;
        }

        return null;
    }

    /**
     * Check if the device supports compass orientation
     */
    isSupported(): boolean {
        return typeof DeviceOrientationEvent !== 'undefined';
    }

    /**
     * Check if currently listening to orientation events
     */
    isActive(): boolean {
        return this.isListening;
    }
}
